import { type Plugin, type Ref, inject } from 'vue'
import { createI18n } from 'vue-i18n'

import type { ImportWrapper } from '@/types'

import { LOCALE_IMPORT_MAP, SUPPORTED_LOCALES } from './helpers'
import type { AvailableLocale } from './types'

export type I18NAddon = {
    currentLocale: Ref<AvailableLocale>
    getCurrentLanguage: () => string
    getSupportedLocales: () => AvailableLocale[]
    getDefaultLocale: () => AvailableLocale
    getPreferredLocale: () => AvailableLocale | undefined
    setPreferredLocale: (locale: AvailableLocale) => void
    loadLocale: (params: { locale: string; registerImport: ImportWrapper }) => Promise<AvailableLocale>
}

const DEFAULT_LOCALE: AvailableLocale = 'en-US'

const I18N_ADDON_SYMBOL = Symbol('i18nAddon')

export const createI18nWithAddon = ({
    enableI18n,
    detectedLocale,
}: {
    enableI18n: boolean
    detectedLocale?: AvailableLocale
}) => {
    const vueI18n = createI18n({
        legacy: false,
        locale: '',
        messages: {},
        pluralRules: {
            'pl-PL': (choice, choicesLength) => {
                const n = Math.abs(Number(choice))
                if (choicesLength === 1) {
                    return 0
                }

                if (choicesLength === 2) {
                    return n === 1 ? 0 : 1
                }

                if (!Number.isInteger(n)) {
                    return 2
                }

                const mod10 = n % 10
                const mod100 = n % 100

                if (n === 1) {
                    return 0
                }

                if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
                    return 1
                }

                return 2
            },
        },
    })

    let preferredLocale = detectedLocale

    // TODO turn into class
    const i18nAddon: I18NAddon & Plugin = {
        currentLocale: vueI18n.global.locale as Ref<AvailableLocale>,
        getCurrentLanguage() {
            // used in SDK. Games receive languages, not locales
            return this.currentLocale.value.split('-')[0] || 'en'
        },
        getSupportedLocales() {
            if (enableI18n) {
                return SUPPORTED_LOCALES
            }
            return [DEFAULT_LOCALE]
        },
        getDefaultLocale() {
            return DEFAULT_LOCALE
        },
        getPreferredLocale() {
            if (enableI18n) {
                return preferredLocale
            }
            return undefined
        },
        setPreferredLocale(locale) {
            preferredLocale = locale
        },
        async loadLocale({ locale, registerImport }) {
            // Use the requested locale if it exists in the map, otherwise use default
            const loadedLocale = locale in LOCALE_IMPORT_MAP ? (locale as AvailableLocale) : DEFAULT_LOCALE
            const importCallback = LOCALE_IMPORT_MAP[loadedLocale]

            const json = (await registerImport(importCallback)()).default
            vueI18n.global.setLocaleMessage(loadedLocale, json)
            vueI18n.global.locale.value = loadedLocale
            return loadedLocale
        },
        install(app) {
            vueI18n.install(app)
            app.provide(I18N_ADDON_SYMBOL, i18nAddon)

            app.mixin({
                beforeCreate() {
                    // re-exporting $t from i18n with strict types
                    // oxlint-disable-next-line
                    this.$tr = this.$t
                },
                unmounted() {
                    delete this.$tr
                },
            })
        },
    }

    return { vueI18n, i18nAddon }
}

export const useI18nAddon = (): I18NAddon => {
    const i18nAddon = inject<I18NAddon>(I18N_ADDON_SYMBOL)
    if (!i18nAddon) {
        throw new Error('i18nAddon not found')
    }
    return i18nAddon
}
